import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useStudies } from "@/api/hooks/studies";
import { useDiseaseAreas } from "@/api/hooks/diseaseAreas";
import {
  STUDY_KIND_LABELS,
  STUDY_KINDS,
  STUDY_STATUS_LABELS,
  STUDY_TYPE_LABELS,
} from "@/api/labels";
import type { StudyKind } from "@/api/types";

export default function StudiesList() {
  const [diseaseAreaId, setDiseaseAreaId] = useState<string>("");
  const [kind, setKind] = useState<StudyKind | "">("");

  const { data: areas } = useDiseaseAreas();
  const { data, isLoading, error } = useStudies({
    disease_area_id: diseaseAreaId || undefined,
    kind: (kind || undefined) as StudyKind | undefined,
  });

  const areasById = new Map((areas ?? []).map((a) => [a.id, a.code]));

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5">Studies</Typography>
        <Link to="new" style={{ textDecoration: "none" }}>
          <Button variant="contained" startIcon={<Add />}>
            New
          </Button>
        </Link>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Disease area"
          value={diseaseAreaId}
          onChange={(e) => setDiseaseAreaId(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {(areas ?? []).map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.code}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as StudyKind | "")}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {STUDY_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {STUDY_KIND_LABELS[k]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">{error.message}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Disease area</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Sponsor</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((s) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                  {s.code}
                </TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>{areasById.get(s.disease_area_id) ?? "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={STUDY_KIND_LABELS[s.kind]}
                    color={s.kind === "master" ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell>{s.sponsor ?? "—"}</TableCell>
                <TableCell>{STUDY_TYPE_LABELS[s.study_type]}</TableCell>
                <TableCell>{STUDY_STATUS_LABELS[s.status]}</TableCell>
                <TableCell align="right">
                  <Link to={`${s.id}/edit`}>Edit</Link>
                </TableCell>
              </TableRow>
            ))}
            {(data ?? []).length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No studies match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
